import { useState } from "react";
import { Warning } from "@phosphor-icons/react";
import { uploadFile } from "@/lib/storage/upload";
import { createTake } from "@/lib/firestore/takes";

interface CrashRecoveryBannerProps {
  projectId: string;
  nextOrder: number;
  mimeType: string;
  fileExtension: string;
  onRecovered: () => void;
  onDiscard: () => void;
  recoverChunks: () => Promise<Blob | null>;
  clearRecovery: () => Promise<void>;
}

export function CrashRecoveryBanner({
  projectId, nextOrder, mimeType, fileExtension,
  onRecovered, onDiscard, recoverChunks, clearRecovery,
}: CrashRecoveryBannerProps) {
  const [recovering, setRecovering] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);

  const handleRecover = async () => {
    setRecovering(true);
    setRecoveryError(null);
    try {
      const blob = await recoverChunks();
      if (!blob) { await clearRecovery(); onDiscard(); return; }

      const file = new File([blob], `recovered-${Date.now()}.${fileExtension}`, { type: mimeType });
      const storagePath = await uploadFile(file, projectId, "audio");
      const durationEstimate = Math.round(blob.size / (64000 / 8)); // rough: 64kbps → bytes/sec

      await createTake(projectId, {
        type: "recording",
        title: `Recovered Recording`,
        duration_seconds: durationEstimate,
        storage_path: storagePath,
        format: fileExtension,
        mime_type: mimeType,
        size_bytes: blob.size,
        status: "ready",
        order: nextOrder,
      });

      await clearRecovery();
      onRecovered();
    } catch (err) {
      setRecoveryError(err instanceof Error ? err.message : "Could not recover the saved source.");
      setRecovering(false);
    }
  };

  const handleDiscard = async () => {
    await clearRecovery();
    onDiscard();
  };

  return (
    <div className="mb-6 rounded-as-3 bg-walnut-tint border border-walnut p-5">
      <div className="flex items-start gap-3">
        <Warning size={20} weight="bold" className="mt-1 flex-shrink-0 text-walnut-deep" />
        <div className="flex-1">
          <p className="font-display font-semibold text-t-18 text-ink-1">Unfinished source recovered.</p>
          <p className="mt-1 font-text text-t-14 leading-as-read text-ink-2">
            A previous capture was interrupted. The audio is still here and can
            be added to the book source.
          </p>
          {recoveryError && (
            <p className="mt-2 font-mono text-[11px] uppercase tracking-as-eyebrow text-flag">
              {recoveryError}
            </p>
          )}
          <div className="mt-4 flex gap-3 items-center">
            <button
              onClick={handleRecover}
              disabled={recovering}
              className="inline-flex items-center justify-center font-ui text-t-13 font-semibold rounded-as-2 px-[14px] py-[7px] bg-walnut text-paper-raised shadow-as-1 hover:bg-walnut-deep transition-colors duration-as-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {recovering ? "Recovering source..." : "Add to source"}
            </button>
            <button
              onClick={handleDiscard}
              disabled={recovering}
              className="font-mono text-[11px] tracking-as-eyebrow uppercase text-ink-2 hover:text-walnut-deep transition-colors duration-as-1 disabled:opacity-50"
            >
              Discard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
